<template>
	<!-- Horizontal bar chart drawn to scale: every bar is value / axisMax of the
	     track width, the axis max comes from posNiceScale, and the tick labels
	     sit at their true positions. Optional stacked series (e.g. cash vs
	     transfer) share the same scale. -->
	<div class="pos-bars">
		<div v-if="!rows.length" class="text-muted text-center py-3">{{ emptyText }}</div>
		<template v-else>
			<div v-if="series.length > 1" class="pos-bars__legend">
				<span v-for="s in series" :key="s.key" class="mr-3">
					<i class="pos-bars__swatch" :style="{ background: s.color }"></i>{{ s.label }}
				</span>
			</div>
			<div v-for="(r, i) in rows" :key="i" class="pos-bars__row">
				<div class="pos-bars__label" :title="r.label">{{ r.label }}</div>
				<div class="pos-bars__track">
					<div
						v-for="s in series"
						:key="s.key"
						class="pos-bars__seg"
						:style="{ width: pct(r.values[s.key]) + '%', background: s.color }"
						:title="s.label + ': ' + fmt(r.values[s.key])"
					></div>
				</div>
				<div class="pos-bars__value">{{ fmt(total(r)) }}</div>
			</div>
			<div class="pos-bars__row pos-bars__axis">
				<div class="pos-bars__label"></div>
				<div class="pos-bars__track pos-bars__track--axis">
					<span
						v-for="(t, k) in scale.ticks"
						:key="k"
						class="pos-bars__tick"
						:style="{ left: pct(t) + '%' }"
					>{{ fmt(t) }}</span>
				</div>
				<div class="pos-bars__value"></div>
			</div>
		</template>
	</div>
</template>

<script>
import { posNiceScale, posBarPct, posMoney, posInt } from "@/utils/pos";

export default {
	name: "PosBarChart",
	props: {
		// [{ label, values: { <seriesKey>: number } }]
		rows: { type: Array, default: () => [] },
		// [{ key, label, color }] — stacked left to right
		series: { type: Array, default: () => [] },
		money: { type: Boolean, default: true },
		emptyText: { type: String, default: "—" }
	},
	computed: {
		scale() {
			const max = this.rows.reduce((m, r) => Math.max(m, this.total(r)), 0);
			return posNiceScale(max, 4, !this.money);
		}
	},
	methods: {
		total(r) {
			return this.series.reduce((s, x) => s + (Number(r.values && r.values[x.key]) || 0), 0);
		},
		pct(v) {
			return posBarPct(v, this.scale.max);
		},
		fmt(v) {
			return this.money ? posMoney(v) : posInt(v);
		}
	}
};
</script>

<style>
.pos-bars {
	font-size: 0.8125rem;
}
.pos-bars__legend {
	margin-bottom: 0.5rem;
}
.pos-bars__swatch {
	display: inline-block;
	width: 10px;
	height: 10px;
	margin-right: 4px;
	border-radius: 2px;
}
.pos-bars__row {
	display: flex;
	align-items: center;
	margin-bottom: 4px;
}
.pos-bars__label {
	flex: 0 0 180px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	padding-right: 8px;
}
.pos-bars__track {
	position: relative;
	flex: 1 1 auto;
	display: flex;
	height: 18px;
	background: #f6f9fc;
	border-radius: 3px;
	overflow: hidden;
}
.pos-bars__track--axis {
	background: transparent;
	height: 16px;
	overflow: visible;
	border-top: 1px solid #dee2e6;
}
.pos-bars__seg {
	height: 100%;
}
.pos-bars__tick {
	position: absolute;
	top: 2px;
	transform: translateX(-50%);
	color: #525f7f;
	font-size: 0.6875rem;
	white-space: nowrap;
}
.pos-bars__value {
	flex: 0 0 110px;
	text-align: right;
	padding-left: 8px;
	font-variant-numeric: tabular-nums;
}
</style>
